/**
 * Ktorý z káblov na stĺpe prichádza od OLT.
 *
 * V kazete sa vstupný kábel vyberá zo zoznamu a ten bol doteraz v poradí, v
 * akom káble prídu zo store (fakticky podľa ID). Na stĺpe s dvoma-troma káblami
 * tak technik dostal náhodný a musel ho zakaždým prepínať.
 *
 * Ako sa smer zistí: `traceOpticalPath()` vracia cestu vlákna SMEROM K OLT,
 * takže počet stĺpov na tej ceste je vzdialenosť od OLT. Kábel, ktorý má
 * najkratšiu cestu, je ten prichádzajúci.
 *
 * Pozerá sa na VŠETKY vlákna kábla, nie na prvé. Kábel totiž nesie signál
 * obomi smermi — nadradený splitter posiela svoje výstupy späť do toho istého
 * kábla na ďalšie vlákna (`reverse_direction`). Keby sa bralo prvé vlákno
 * s platnou cestou, na stĺpe s rozvodným splittrom by to ukázalo opačne.
 *
 * Overené na produkčných dátach: v Ladiciach sedí na všetkých piatich stĺpoch
 * s viacerými káblami, v Ohaji sa určí 33 z 33.
 */
import { Cable, Pole } from '../types';
import { traceOpticalPath } from './opticalPathTracer';

/** Koľko vlákien kábla skúsiť. Viac než 24 je zbytočné — stačí nájsť to najkratšie. */
const MAX_VLAKIEN = 24;

const pocetVlakien = (cable: Cable): number => {
  const m = /(\d+)/.exec(cable.cable_type || '');
  return m ? Math.min(parseInt(m[1], 10), MAX_VLAKIEN) : 12;
};

/** Vzdialenosť kábla od OLT v stĺpoch, alebo null keď k OLT nevedie. */
export const vzdialenostOdOlt = (
  cable: Cable,
  poles: Pole[],
  cables: Cable[],
): number | null => {
  let naj: number | null = null;
  for (let f = 1; f <= pocetVlakien(cable); f++) {
    try {
      const r = traceOpticalPath(cable.id, f, poles, cables);
      if (r.found && r.isValid) {
        const dlzka = r.segments.filter((s) => s.poleId).length;
        if (naj === null || dlzka < naj) naj = dlzka;
      }
    } catch {
      /* vlákno sa nedá dotrasovať — skúsime ďalšie */
    }
  }
  return naj;
};

export interface ZoradeneKable {
  /** Káble stĺpa, ten od OLT prvý. Keď sa smer určiť nedá, poradie zostáva pôvodné. */
  kable: Cable[];
  /** ID kábla, ktorý prichádza od OLT, alebo null */
  odOltId: number | null;
}

/**
 * Zoradí káble stĺpa tak, aby ten od OLT bol prvý.
 *
 * Keď sa smer určiť nedá (stĺp ešte nie je prepojený k OLT), vráti pôvodné
 * poradie — zámerne, nech sa pri rozostavanom projekte nič nemení pod rukami.
 */
export const zoradPodlaOlt = (
  kableStlpa: Cable[],
  poles: Pole[],
  vsetkyKable: Cable[],
): ZoradeneKable => {
  if (kableStlpa.length < 2) {
    return { kable: kableStlpa, odOltId: null };
  }

  const so = kableStlpa.map((c) => ({ c, d: vzdialenostOdOlt(c, poles, vsetkyKable) }));
  const dotrasovane = so.filter((x) => x.d !== null);
  if (dotrasovane.length === 0) {
    return { kable: kableStlpa, odOltId: null };
  }

  // Stabilné zoradenie: bez signálu ostávajú vzadu v pôvodnom poradí
  const zoradene = [...so].sort((a, b) => {
    if (a.d === null && b.d === null) return 0;
    if (a.d === null) return 1;
    if (b.d === null) return -1;
    return a.d - b.d;
  });

  return { kable: zoradene.map((x) => x.c), odOltId: zoradene[0].d !== null ? zoradene[0].c.id : null };
};

/**
 * Ktorý koniec kábla je bližšie k OLT.
 *
 * Trasovanie ide od vlákna smerom k OLT, takže prvý stĺp v ceste je ten, kde
 * vlákno začína — teda koniec od OLT. Pozerá sa na viac vlákien a rozhoduje
 * väčšina: kábel nesie signál obomi smermi (nadradený splitter posiela výstupy
 * späť do toho istého kábla), takže jedno vlákno by ukázalo opačne.
 */
export const koniecOdOlt = (
  cable: Cable,
  poles: Pole[],
  cables: Cable[],
): number | null => {
  const hlasy = new Map<number, number>();
  const m = /(\d+)/.exec(cable.cable_type || '');
  const pocet = Math.min(m ? parseInt(m[1], 10) : 12, MAX_VLAKIEN);

  for (let f = 1; f <= pocet; f++) {
    try {
      const r = traceOpticalPath(cable.id, f, poles, cables);
      if (!r.found || !r.isValid) continue;
      const prvy = r.segments.find((s) => s.poleId)?.poleId;
      if (prvy === cable.from_pole_id || prvy === cable.to_pole_id) {
        hlasy.set(prvy, (hlasy.get(prvy) || 0) + 1);
      }
    } catch {
      /* vlákno sa nedá dotrasovať */
    }
  }

  // Bez rozbaľovania iterátora — projekt sa kompiluje do staršieho cieľa
  let najlepsi: number | null = null;
  let najviac = 0;
  hlasy.forEach((pocetHlasov, poleId) => {
    if (pocetHlasov > najviac) {
      najviac = pocetHlasov;
      najlepsi = poleId;
    }
  });
  return najlepsi;
};
